import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid'
import { ChevronDownIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline'

export type BillingPeriod = 'monthly' | 'quarterly' | 'yearly'

export interface Plan {
  id: number
  slug: string
  name: string
  description?: string
  target_audience?: string
  plan_highlights?: string
  price_min: number
  price_max: number
  duration_days: number
  plan_type: string
  billing_period: BillingPeriod
  discount_percent: number
  final_price: number
  savings: number
  is_active: boolean
  is_popular?: boolean
  highlight_badge?: string | null
  sort_order?: number
  core_details?: string
  email_delivery: boolean
  telegram_delivery: boolean
  whatsapp_support: boolean
  priority_support: boolean
  monthly_outlook: boolean
  weekly_sectoral_reports: boolean
  custom_research_reports: boolean
  market_radar_newsletter: boolean
  intraday_trades: boolean
  positional_trades: boolean
  strategy_based_options: boolean
  portfolio_tracker?: string
  portfolio_audit_frequency?: string
  portfolio_rebalancing: boolean
  risk_management_plan: boolean
  watchlist_access: boolean
  alerts_access: boolean
  risk_meter_access: boolean
  live_market_access: boolean
  review_calls?: string
  call_support: boolean
  compliance_dashboard: boolean
  net_worth_certificate: boolean
  basic_education_content: boolean
}

export interface SharedPlanCardProps {
  plan: Plan
  popular: boolean
  isCurrentPlan?: boolean
  onGetStarted?: (plan: Plan) => void
}

const SHOW_LIMIT = 6

export const BILLING: Record<BillingPeriod, { label: string; months: number; unit: string; billedAs: string }> = {
  monthly:   { label: 'Monthly',   months: 1,  unit: 'month',   billedAs: 'monthly' },
  quarterly: { label: 'Quarterly', months: 3,  unit: 'quarter', billedAs: 'quarterly' },
  yearly:    { label: 'Yearly',    months: 12, unit: 'year',    billedAs: 'annually' },
}

export const BillingToggle: React.FC<{
  value: BillingPeriod
  periods: BillingPeriod[]
  onChange: (b: BillingPeriod) => void
}> = ({ value, periods, onChange }) => (
  <div className="inline-flex items-center gap-1.5 rounded-full border border-dark-600 bg-dark-700/80 p-1.5 shadow-lg ring-1 ring-white/5">
    {periods.map(period => {
      const active = value === period
      return (
        <div key={period} className="relative">
          {period === 'quarterly' && (
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10">
              <span className="bg-green-500 text-white px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-lg whitespace-nowrap border border-dark-900">
                Save
              </span>
            </div>
          )}
          {period === 'yearly' && (
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10">
              <span className="bg-amber-500 text-black px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-lg whitespace-nowrap border border-dark-900">
                Best
              </span>
            </div>
          )}
          <button
            type="button"
            onClick={() => onChange(period)}
            className={`relative rounded-full px-6 py-2.5 text-sm font-bold transition-all duration-200 ${
              active
                ? 'bg-emerald-500 text-black shadow-md shadow-emerald-500/30'
                : 'text-gray-300 hover:bg-dark-600 hover:text-white'
            }`}
          >
            {BILLING[period].label}
          </button>
        </div>
      )
    })}
  </div>
)

function computePricing(plan: Plan) {
  const { months, unit, billedAs } = BILLING[plan.billing_period] ?? BILLING.monthly
  const fullPrice = Number(plan.price_min)
  const total = Number(plan.final_price ?? plan.price_min)
  const savings = Number(plan.savings ?? (fullPrice - total))
  const discount = Number(plan.discount_percent)
  const perMonth = Math.round(total / months)
  return { total, perMonth, fullPrice, savings, months, discount, unit, billedAs }
}

function getPlanFeaturesList(plan: Plan) {
  const dynamicFeatures: {label: string, included: boolean}[] = []
  try {
    if (plan.core_details) {
      const parsed = JSON.parse(plan.core_details)
      if (Array.isArray(parsed)) {
        parsed.forEach(p => {
           dynamicFeatures.push({ label: p.value, included: true })
        })
      }
    }
  } catch {}

  return [
    ...dynamicFeatures,
    { label: 'Email delivery', included: plan.email_delivery },
    { label: 'Telegram alerts', included: plan.telegram_delivery },
    { label: 'WhatsApp support', included: plan.whatsapp_support },
    { label: 'Priority support', included: plan.priority_support },
    { label: 'Monthly market outlook', included: plan.monthly_outlook },
    { label: 'Weekly sectoral reports', included: plan.weekly_sectoral_reports },
    { label: 'Custom research reports', included: plan.custom_research_reports },
    { label: 'Market radar newsletter', included: plan.market_radar_newsletter },
    { label: 'Intraday calls', included: plan.intraday_trades },
    { label: 'Positional calls', included: plan.positional_trades },
    { label: 'Strategy-based F&O', included: plan.strategy_based_options },
    { label: plan.portfolio_tracker ? `${plan.portfolio_tracker.charAt(0).toUpperCase() + plan.portfolio_tracker.slice(1)} portfolio tracker` : 'Portfolio tracker', included: !!plan.portfolio_tracker },
    { label: plan.portfolio_audit_frequency ? `${plan.portfolio_audit_frequency.charAt(0).toUpperCase() + plan.portfolio_audit_frequency.slice(1)} portfolio audit` : 'Portfolio audit', included: !!plan.portfolio_audit_frequency },
    { label: 'Portfolio rebalancing', included: plan.portfolio_rebalancing },
    { label: 'Risk management plan', included: plan.risk_management_plan },
    { label: 'Watchlist access', included: plan.watchlist_access },
    { label: 'Price alerts', included: plan.alerts_access },
    { label: 'Risk meter', included: plan.risk_meter_access },
    { label: 'Live market access', included: plan.live_market_access },
    { label: plan.review_calls ? `${plan.review_calls} review calls` : 'Review calls', included: !!plan.review_calls },
    { label: 'Call support', included: plan.call_support },
    { label: 'Compliance dashboard', included: plan.compliance_dashboard },
    { label: 'Net worth certificate', included: plan.net_worth_certificate },
    { label: 'Education content', included: plan.basic_education_content },
  ]
}

export const SharedPlanCard: React.FC<SharedPlanCardProps> = ({ plan, popular, isCurrentPlan, onGetStarted }) => {
  const [expanded, setExpanded] = useState(false)
  const isPopular = popular
  const pricing = computePricing(plan)
  const features = getPlanFeaturesList(plan)
  
  const includedFeatures = features.filter(f => f.included)
  const excludedFeatures = features.filter(f => !f.included)
  
  // Sort features to show included first
  const displayFeatures = [...includedFeatures, ...excludedFeatures]
  const visible = expanded ? displayFeatures : displayFeatures.slice(0, SHOW_LIMIT)
  const hiddenCount = displayFeatures.length - SHOW_LIMIT

  let parsedHighlights: string[] = []
  try {
    if (plan.plan_highlights) {
      const parsed = JSON.parse(plan.plan_highlights)
      if (Array.isArray(parsed)) parsedHighlights = parsed
    }
  } catch (e) {
    if (typeof plan.plan_highlights === 'string') {
      parsedHighlights = [plan.plan_highlights]
    }
  }

  // Visual Design overrides: use global theme variables
  const cardBg = 'bg-white dark:bg-dark-900 bg-dark-900' // Fallback to bg-dark-900 as app defaults to dark
  const cardBorder = isPopular ? 'border-amber-500 shadow-2xl shadow-amber-500/10' : 'border-dark-700'

  return (
    <div className="relative flex flex-col pt-4 h-full w-full">
      {/* Badge above card */}
      {isPopular && (
        <div className="absolute top-0 left-1/2 -translate-x-1/2 z-10">
          <span className="bg-amber-500 text-black px-4 py-1 rounded-full text-xs font-bold flex items-center gap-1.5 shadow-lg whitespace-nowrap border-2 border-dark-900">
            <StarSolidIcon className="w-3 h-3" />
            {plan.highlight_badge || 'Most Popular'}
          </span>
        </div>
      )}

      <div className={`group flex flex-col h-full rounded-2xl border-2 ${cardBg} ${cardBorder} p-6 transition-all duration-300 hover:-translate-y-1.5`}>
        
        {/* Title */}
        <div className="mb-1 text-center">
          <h3 className="text-2xl font-bold text-white uppercase tracking-wider">
            {plan.name}
          </h3>
        </div>

        {/* Target Audience (Replaces description) */}
        {plan.target_audience && (
          <p className="text-sm text-gray-400 text-center mb-5">
            <span className="font-semibold text-gray-300">Target:</span> {plan.target_audience}
          </p>
        )}

        {/* Price block */}
        <div className="mb-6 text-center flex flex-col items-center transition-all duration-300 group-hover:scale-105">
          {pricing.savings > 0 && (
            <p className="text-sm text-gray-500 line-through mb-1 transition-colors duration-300 group-hover:text-gray-400">
              ₹{pricing.fullPrice.toLocaleString('en-IN')}/{pricing.unit}
            </p>
          )}
          <div className="flex items-baseline gap-1 justify-center">
            <span className="text-2xl text-gray-300 font-semibold transition-colors duration-300 group-hover:text-blue-400">₹</span>
            <span className="text-5xl font-bold text-white transition-all duration-300 group-hover:text-blue-400 group-hover:drop-shadow-[0_0_15px_rgba(59,130,246,0.5)]">
              {pricing.total.toLocaleString('en-IN')}
            </span>
          </div>
          <p className="text-sm text-gray-500 mt-2 transition-colors duration-300 group-hover:text-gray-400">
            / {BILLING[plan.billing_period]?.label || 'Month'}
          </p>
        </div>

        {/* CTAs */}
        <div className="flex flex-col gap-3 mb-8">
          <Link to={`/plans/${plan.slug}`} className="block w-full">
            <button type="button" className="w-full rounded-xl py-3.5 text-sm font-bold border-2 border-white text-white hover:bg-white/10 transition-colors bg-transparent">
              View Plan
            </button>
          </Link>
          {isCurrentPlan ? (
             <div className="w-full py-3.5 rounded-xl bg-green-500/10 border border-green-500/30 text-green-400 text-sm font-bold text-center">
               ✓ Current Plan
             </div>
          ) : (
            <button
              type="button"
              className="w-full bg-[#3B82F6] hover:bg-blue-600 text-white rounded-xl py-3.5 text-sm font-bold transition-colors"
              onClick={() => onGetStarted && onGetStarted(plan)}
            >
              Get {plan.name}
            </button>
          )}
        </div>

        {/* Plan Highlights */}
        {parsedHighlights.length > 0 && (
          <div className="bg-dark-800 border border-blue-500/30 rounded-xl p-4 mb-6">
            <h4 className="font-bold text-blue-100 flex items-center gap-2 mb-3">
              <span>✨</span> Plan Highlights
            </h4>
            <ul className="space-y-2">
              {parsedHighlights.map((highlight, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-blue-200/80">
                  <span className="text-blue-400 font-bold mt-0.5">→</span>
                  <span>{highlight}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Features & Benefits */}
        <div className="flex-1 space-y-3">
          <h4 className="font-bold text-white mb-4">Features & Benefits:</h4>
          {visible.map((feature, i) => (
            <div key={i} className="flex items-start gap-3">
              {feature.included ? (
                <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              ) : (
                <XMarkIcon className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
              )}
              <span className={`text-sm ${feature.included ? 'text-gray-300' : 'text-gray-600 line-through'}`}>
                {feature.label}
              </span>
            </div>
          ))}

          {/* Show more / less */}
          {displayFeatures.length > SHOW_LIMIT && (
            <button
              type="button"
              onClick={() => setExpanded(e => !e)}
              className="flex items-center gap-1 text-sm font-semibold text-blue-400 hover:text-blue-300 transition-colors mt-4 focus:outline-none w-full justify-center py-2"
            >
              {expanded ? 'Show less' : `Show ${hiddenCount} more features`}
              <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
            </button>
          )}
        </div>

      </div>
    </div>
  )
}
